import React, { Component } from "react";
import { toastManager } from "../../services/toastManager";
import { useApiRequests } from "../../services/fetchApiServices/index";
import { CustomModal, LoadingSpinner } from "../../common";
import "./styles.scss";

export class ModalEditProduct extends Component {
  constructor(props) {
    super(props);
    const { selectedProduct } = this.props;
    
    this.state = {
      productId: selectedProduct.id || "",
      newProductName: selectedProduct.name || "",
      newProductPrice: selectedProduct.price || 0,
      newProductComment: selectedProduct.comment || "",
      isNewProductAvailable: selectedProduct.isAvailable || false,
      isSecondModalOpen: false,
      isLoading: false,
      isDragging: false,
      selectedFiles: [], // Array to store multiple new files
      imagePreviewUrl: "",
      isLoadingImage: false,
      existingImages: [], // Array to store all existing images
      currentImageIndex: 0, // Index of currently displayed image
      isZoomed: false, // State for zoom modal
    };this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    // Load existing image when component mounts
    const { selectedProduct } = this.props;
    this.loadExistingImage(selectedProduct.id);
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectedProduct !== prevProps.selectedProduct) {
      const { selectedProduct } = this.props;
      // Clean up previous preview URL if it exists
      if (this.isBlobUrl(this.state.imagePreviewUrl)) {
        URL.revokeObjectURL(this.state.imagePreviewUrl);
      }
      // Clean up existing images blob URLs (including new files)
      this.state.existingImages.forEach(img => {
        if (this.isBlobUrl(img.url)) {
          URL.revokeObjectURL(img.url);
        }
      });
      
      this.setState({
        newProductName: selectedProduct.name || "",
        newProductPrice: selectedProduct.price || 0,
        newProductComment: selectedProduct.comment || "",
        isNewProductAvailable: selectedProduct.isAvailable || false,
        selectedFiles: [], // Clear selected files
        imagePreviewUrl: "",
        existingImages: [],
        currentImageIndex: 0,
      });
      
      // Download existing images if available
      this.loadExistingImage(selectedProduct.id);
    }
  }

  // Helper method to check if a URL is a blob URL
  isBlobUrl = (url) => {
    return typeof url === 'string' && url.startsWith('blob:');
  };

  // Helper method to reset image state
  resetImageState = (images = [], index = 0, previewUrl = "") => {
    this.setState({
      existingImages: images,
      currentImageIndex: index,
      imagePreviewUrl: previewUrl,
      isLoadingImage: false
    });
  };

  loadExistingImage = async (productId) => {
      try {
        this.setState({ isLoadingImage: true });
        const apiRequests = useApiRequests();

        // Get metadata for all images for this product
        const metadata = await apiRequests.getAllFilesAsMetadata(productId);
        console.log('Product images metadata:', metadata);
      
        // Check if there are any images for this product
        if (metadata && metadata.length > 0) {
          // Download all images
          const imagePromises = metadata.map(async (imageInfo) => {
            try {
              const blobName = imageInfo.name;
              const downloadedImageUrl = await apiRequests.downloadFile(blobName);
              return {
                url: downloadedImageUrl,
                name: imageInfo.name,
                size: imageInfo.size,
                metadata: imageInfo
              };
            } catch (error) {
              console.error('Error downloading image:', imageInfo.name, error);
              return null;
            }
          });

          const downloadedImages = await Promise.all(imagePromises);
          const validImages = downloadedImages.filter(img => img !== null);

          if (validImages.length > 0) {
            this.resetImageState(validImages, 0, validImages[0].url);
          } else {
            this.resetImageState();
          }
        } else {
          // No images found for this product
          this.resetImageState();
        }
      } catch (error) {
        console.error('Error loading existing images:', error);
        this.resetImageState();
      }
  };

  componentWillUnmount() {
    // Clean up blob URLs to prevent memory leaks
    if (this.isBlobUrl(this.state.imagePreviewUrl)) {
      URL.revokeObjectURL(this.state.imagePreviewUrl);
    }
    // Clean up existing images blob URLs
    this.state.existingImages.forEach(img => {
      if (this.isBlobUrl(img.url)) {
        URL.revokeObjectURL(img.url);
      }
    });
  }

  // Generic navigation helper method
  navigateImage = (direction) => {
    const { existingImages, currentImageIndex } = this.state;
    if (existingImages.length === 0) return;

    let newIndex;
    if (direction === 'previous') {
      newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : existingImages.length - 1;
    } else if (direction === 'next') {
      newIndex = currentImageIndex < existingImages.length - 1 ? currentImageIndex + 1 : 0;
    } else {
      return; // Invalid direction
    }

    this.setState({
      currentImageIndex: newIndex,
      imagePreviewUrl: existingImages[newIndex].url
    });
  };

  // Navigation functions for image carousel
  goToPreviousImage = () => {
    this.navigateImage('previous');
  };

  goToNextImage = () => {
    this.navigateImage('next');
  };

  // Zoom functionality
  openZoom = () => {
    this.setState({ isZoomed: true });
  };

  closeZoom = () => {
    this.setState({ isZoomed: false });
  };

  goToPreviousImageZoomed = () => {
    this.navigateImage('previous');
  };

  goToNextImageZoomed = () => {
    this.navigateImage('next');
  };

  deleteCurrentImage = async () => {
    const { existingImages, currentImageIndex } = this.state;
    if (existingImages.length === 0) return;
    
    const currentImage = existingImages[currentImageIndex];
    
    try {
      const apiRequests = useApiRequests();
      await apiRequests.deleteFile(currentImage.name);
      
      // Remove the deleted image from the array
      const updatedImages = existingImages.filter((_, index) => index !== currentImageIndex);
      
      // Clean up the blob URL
      if (this.isBlobUrl(currentImage.url)) {
        URL.revokeObjectURL(currentImage.url);
      }
      
      // Update state using helper method
      if (updatedImages.length > 0) {
        const newIndex = currentImageIndex >= updatedImages.length ? 0 : currentImageIndex;
        this.setState({
          existingImages: updatedImages,
          currentImageIndex: newIndex,
          imagePreviewUrl: updatedImages[newIndex].url
        });
      } else {
        this.resetImageState();
      }
      
      toastManager.success('Image deleted successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  onNewProductNameChange = (event) => {
    this.setState({ newProductName: event.target.value });
  };

  onNewProductPriceChange = (event) => {
    this.setState({ newProductPrice: parseFloat(event.target.value) });
  };

  onNewProductCommentChange = (event) => {
    this.setState({ newProductComment: event.target.value });
  };

  onIsNewProductAvailableChange = (event) => {
    this.setState({ isNewProductAvailable: event.target.value === "Yes" });
  };

  handleOpenConfirmationModal = () => {
    this.setState({ isSecondModalOpen: true });
  };

  handleConfirmEditProduct = async () => {
    this.setState({ isSecondModalOpen: false, isLoading: true });
    await this.handleProductEdit();
    this.setState({ isLoading: false });
    this.props.handleModalClose();
  };
  // Helper method to upload product files
  uploadProductFiles = async () => {
    if (this.state.selectedFiles.length === 0) return;
    
    const apiRequests = useApiRequests();
    
    // Upload each file sequentially
    for (const file of this.state.selectedFiles) {
      await apiRequests.uploadFile(file, this.state.productId);
    }
  };

  // Helper method to update product details
  updateProduct = async () => {
    const apiRequests = useApiRequests();
    return await apiRequests.editProduct(
      this.props.selectedProduct.id,
      this.state.newProductName,
      this.state.newProductPrice,
      this.state.newProductComment,
      this.state.isNewProductAvailable
    );
  };

  handleProductEdit = async () => {
    try {
      // Upload all new files if any are selected
      await this.uploadProductFiles();

      // Update product details
      const response = await this.updateProduct();

      if (response.status === 200) {
        this.setState({ isLoading: true }, () => {
          setTimeout(() => {
            this.setState({ isLoading: false });
            this.props.fetchData();
          }, 700);
        });
      } else {
        // Handle error if needed
      }
    } catch (error) {
      console.log("Error updating product:", error.message);
    }
  };
  handleResetForm = () => {
    const { selectedProduct } = this.props;
    
    // Clean up any new file preview URLs and remove new files from the array
    const existingImagesOnly = this.state.existingImages.filter(img => !img.isNewFile);
    
    // Clean up blob URLs for new files
    this.state.existingImages.forEach(img => {
      if (img.isNewFile && this.isBlobUrl(img.url)) {
        URL.revokeObjectURL(img.url);
      }
    });
    
    // Reset to show existing images if any
    const resetImageUrl = existingImagesOnly.length > 0 ? existingImagesOnly[0].url : "";
    
    this.setState({
      newProductName: selectedProduct.name || "",
      newProductPrice: selectedProduct.price || 0,
      newProductComment: selectedProduct.comment || "",
      isNewProductAvailable: selectedProduct.isAvailable || false,
      selectedFiles: [], // Clear all selected files
      imagePreviewUrl: resetImageUrl,
      existingImages: existingImagesOnly,
      currentImageIndex: 0,
    });
  };

  handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  // Helper method to validate and filter image files
  validateAndFilterImageFiles = (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toastManager.warn('Please select at least one image file.');
      return null;
    }

    if (imageFiles.length !== files.length) {
      toastManager.warn(`${files.length - imageFiles.length} non-image files were skipped. Only image files are allowed.`);
    }
    
    return imageFiles;
  };

  // Helper method to create image preview object
  createImagePreview = (file) => {
    const previewUrl = URL.createObjectURL(file);
    return {
      url: previewUrl,
      name: file.name,
      size: file.size,
      metadata: null, // This is a new file, no metadata yet
      isNewFile: true, // Flag to identify it as a new upload
      file: file // Store the actual file for uploading later
    };
  };

  handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });

    const files = Array.from(e.dataTransfer.files); // Convert FileList to Array
    const imageFiles = this.validateAndFilterImageFiles(files);
    
    if (!imageFiles) return;
    
    // Process all image files at once
    this.handleMultipleFileSelection(imageFiles);
  };

  handleFileSelect = async (e) => {
    const files = Array.from(e.target.files); // Convert FileList to Array
    const imageFiles = this.validateAndFilterImageFiles(files);
    
    if (!imageFiles) {
      e.target.value = '';
      return;
    }
    
    // Process all image files at once
    this.handleMultipleFileSelection(imageFiles);
    
    // Clear the input so the same files can be selected again
    e.target.value = '';
  };

  handleFileSelection = (file) => {
    // Create a preview URL for the image
    const previewUrl = URL.createObjectURL(file);
    
    // Create a new image object for the preview
    const newImagePreview = {
      url: previewUrl,
      name: file.name,
      size: file.size,
      metadata: null, // This is a new file, no metadata yet
      isNewFile: true, // Flag to identify it as a new upload
      file: file // Store the actual file for uploading later
    };
    
    // Add the new image to the existing images array and set it as current
    const updatedImages = [...this.state.existingImages, newImagePreview];
    const newCurrentIndex = updatedImages.length - 1; // Set to the new image
    
    // Add the file to selectedFiles array
    const updatedSelectedFiles = [...this.state.selectedFiles, file];
    
    this.setState({ 
      selectedFiles: updatedSelectedFiles, 
      imagePreviewUrl: previewUrl,
      existingImages: updatedImages,
      currentImageIndex: newCurrentIndex
    });
  };
  handleMultipleFileSelection = (files) => {
    // Consistent approach: process all files and update state in one setState call
    const newImagePreviews = files.map(file => this.createImagePreview(file));
    this.setState(prevState => {
      const updatedImages = [...prevState.existingImages, ...newImagePreviews];
      const updatedSelectedFiles = [...prevState.selectedFiles, ...files];
      const newCurrentIndex = prevState.existingImages.length; // Index of first new image
      return {
        selectedFiles: updatedSelectedFiles,
        existingImages: updatedImages,
        currentImageIndex: newCurrentIndex,
        imagePreviewUrl: newImagePreviews.length > 0 ? newImagePreviews[0].url : prevState.imagePreviewUrl
      };
    });
  };
  // Helper method to render form fields
  renderFormFields = () => {
    return (
      <>
        <div className="form-group">
          <label
            htmlFor="name"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Product Name:
          </label>
          <div className="col-12">
            <input
              className="form-control"
              name="name"
              type="text"
              id="name"
              value={this.state.newProductName}
              onChange={this.onNewProductNameChange}
              placeholder="Product Name"
            />
          </div>
        </div>

        <div className="form-group">
          <label
            htmlFor="price"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Price:
          </label>
          <div className="col-12">
            <input
              className="form-control"
              name="price"
              id="price"
              type="number"
              value={this.state.newProductPrice}
              onChange={this.onNewProductPriceChange}
              placeholder="Price"
            />
          </div>
        </div>

        <div className="form-group">
          <label
            htmlFor="comment"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Comment:
          </label>
          <div className="col-12">
            <textarea
              className="form-control"
              name="comment"
              id="comment"
              value={this.state.newProductComment}
              onChange={this.onNewProductCommentChange}
              placeholder="Comment"
              rows="8"
              style={{ resize: 'none' }}
            />
          </div>
        </div>

        <div className="form-group">
          <label
            htmlFor="availability"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Is product available?:
          </label>
          <div className="col-12">
            <select
              className="form-control"
              onChange={this.onIsNewProductAvailableChange}
              value={this.state.isNewProductAvailable ? "Yes" : "No"}
            >
              <option>No</option>
              <option>Yes</option>
            </select>
          </div>
        </div>
      </>
    );
  };

  // Helper method to render image navigation controls
  renderImageNavigation = () => {
    if (this.state.existingImages.length <= 1) return null;

    return (
      <>
        <button
          className="nav-arrow nav-arrow-left"
          onClick={(e) => {
            e.stopPropagation();
            this.goToPreviousImage();
          }}
        >
          ‹
        </button>
        <button
          className="nav-arrow nav-arrow-right"
          onClick={(e) => {
            e.stopPropagation();
            this.goToNextImage();
          }}
        >
          ›
        </button>
        <div className="image-counter">
          {this.state.currentImageIndex + 1} / {this.state.existingImages.length}
        </div>
      </>
    );
  };

  // Helper method to handle image removal
  handleImageRemoval = (e) => {
    e.stopPropagation();
    const currentImage = this.state.existingImages[this.state.currentImageIndex];
    
    if (currentImage && currentImage.isNewFile) {
      // This is a new file, remove it from the preview array
      if (this.isBlobUrl(currentImage.url)) {
        URL.revokeObjectURL(currentImage.url);
      }
      
      const updatedImages = this.state.existingImages.filter((_, index) => index !== this.state.currentImageIndex);
      
      // Also remove from selectedFiles array
      const updatedSelectedFiles = this.state.selectedFiles.filter(file => file !== currentImage.file);
      
      if (updatedImages.length > 0) {
        const newIndex = this.state.currentImageIndex >= updatedImages.length ? 0 : this.state.currentImageIndex;
        this.setState({ 
          selectedFiles: updatedSelectedFiles,
          existingImages: updatedImages,
          currentImageIndex: newIndex,
          imagePreviewUrl: updatedImages[newIndex].url
        });
      } else {
        this.setState({ 
          selectedFiles: updatedSelectedFiles,
          existingImages: [],
          currentImageIndex: 0,
          imagePreviewUrl: ""
        });
      }
    } else if (this.state.existingImages.length > 0) {
      // This is an existing image, delete it
      if (window.confirm('Are you sure you want to delete this image?')) {
        this.deleteCurrentImage();
      }
    }
  };

  // Helper method to render image preview content
  renderImagePreview = () => {
    if (this.state.isLoadingImage) {
      return (
        <div className="loading-container">
          <LoadingSpinner />
          <p>Loading images...</p>
        </div>
      );
    }

    if (!this.state.imagePreviewUrl) {
      return (
        <div className="upload-prompt">
          <i className="upload-icon">📁</i>
          <p>
            Drag and drop images here or click to select multiple files
          </p>
        </div>
      );
    }

    return (
      <div className="image-preview">
        <img
          src={this.state.imagePreviewUrl}
          alt="Product preview"
          onClick={(e) => {
            e.stopPropagation();
            this.openZoom();
          }}
          style={{ cursor: 'pointer' }}
        />
        
        {/* Image navigation controls */}
        {this.renderImageNavigation()}
        
        {/* Remove button */}
        <button
          className="remove-image"
          onClick={this.handleImageRemoval}
        >
          &times;
        </button>
      </div>
    );
  };

  // Helper method to render drag and drop zone
  renderDragDropZone = () => {
    const { isDragging, isLoading } = this.state;

    return (
      <div className="form-group">
        <label
          htmlFor="image"
          className="col-xl-4 col-sm-5 col-xs-5 control-label"
        >
          Product Image:
        </label>
        <div className="col-12">
          <div
            className={`drag-drop-zone ${isDragging ? "dragging" : ""}`}
            onDragEnter={this.handleDragEnter}
            onDragLeave={this.handleDragLeave}
            onDragOver={this.handleDragOver}
            onDrop={this.handleDrop}
            onClick={() => this.fileInputRef.current.click()}
          >
            {this.renderImagePreview()}
            <input
              ref={this.fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={this.handleFileSelect}
            />
          </div>
          
          {/* Add Images Button */}
          <div className="add-images-button-container" style={{ textAlign: 'left', marginTop: '10px' }}>
            <button
              type="button"
              className="btn btn-secondary add-images-btn"
              onClick={() => this.fileInputRef.current.click()}
              style={{ fontSize: '12px' }}
            >
              <i className="upload-icon">📁</i>
              Add Images
            </button>
          </div>
          
          {isLoading && <LoadingSpinner />}
        </div>
      </div>
    );
  };

  // Helper method to render modal footer
  renderModalFooter = () => {
    const isFormValid = this.state.newProductName.length && this.state.newProductPrice > 0;
    
    return (
      <div className="modal-footer">
        <button
          type="button"
          className="btn"
          onClick={this.handleResetForm}
        >
          Reset
        </button>
        <button
          type="button"
          className="btn"
          onClick={this.props.handleModalClose}
        >
          Close
        </button>
        <button
          type="button"
          className={isFormValid ? "btn btn-primary" : "btn disabled"}
          onClick={this.handleOpenConfirmationModal}
          disabled={!isFormValid}
        >
          Edit Product
        </button>
      </div>
    );
  };

  // Helper method to render zoom modal
  renderZoomModal = () => {
    if (!this.state.isZoomed) return null;

    return (
      <div className="zoom-modal" onClick={this.closeZoom}>
        <div className="zoom-content" onClick={(e) => e.stopPropagation()}>
          <button className="zoom-close" onClick={this.closeZoom}>
            &times;
          </button>
          
          <img
            src={this.state.imagePreviewUrl}
            alt="Product preview zoomed"
            className="zoomed-image"
          />
          
          {/* Navigation controls for zoom */}
          {this.state.existingImages.length > 1 && (
            <>
              <button
                className="zoom-nav zoom-nav-left"
                onClick={this.goToPreviousImageZoomed}
              >
                ‹
              </button>
              <button
                className="zoom-nav zoom-nav-right"
                onClick={this.goToNextImageZoomed}
              >
                ›
              </button>
              <div className="zoom-counter">
                {this.state.currentImageIndex + 1} / {this.state.existingImages.length}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  render() {
    return (
      <>
        <div
          id="editModal"
          className="modal fade in example-modal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="modal-title-2"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  onClick={this.props.handleModalClose}
                  className="close"
                  aria-label="Close"
                >
                  &times;
                </button>
                <h4 id="modal-title-2" className="modal-title">
                  Edit product
                </h4>
              </div>
              <div className="modal-body">
                {this.renderFormFields()}
                {this.renderDragDropZone()}
              </div>
              {this.renderModalFooter()}
            </div>
          </div>
        </div>

        {this.state.isSecondModalOpen && (
          <CustomModal
            handleModalClose={this.props.handleModalClose}
            handleSubmitForm={this.handleConfirmEditProduct}
            isModalOpen={this.state.isSecondModalOpen}
            message={`Do you want to edit the product: "${this.state.newProductName}"?`}
          />
        )}
        {this.state.isLoading && <LoadingSpinner />}
        {this.renderZoomModal()}
      </>
    );
  }
}